import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { motion, AnimatePresence } from "framer-motion";
import { Auth } from "aws-amplify";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  CreditCard,
  Bell,
  MoreHorizontal,
  X,
  Settings2,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  userData,
  accessToken,
  idToken,
  refreshToken,
  portfolioTreeStore,
  profileImageUrl,
} from "../../store";
import IdleTimeoutContainer from "../../auth/IdleTimeoutContainer";
import { pendingRequestQueue } from "../../services/axiosInterceptor";
import { NotificationContainer } from "../../features/webPush/notificationManager";
import { NotificationsMenuFlag } from "../../featureFlags/mainMenuFlags";
import { useMenuItems } from "./useMenuItems";
import { triggerSnackbar } from "@/components/toastMessage";
import YvooaInfoModal from "@/components/YvooaInfoModal";
import ThemeToggle from "../../features/themeSelect/ThemeToggle";
import { useNotification } from "../../features/notifications/useNotification";
import NotificationSheetContent from "./NotificationSheetContent";
import {
  isPropertyManager,
  isAsstPropertyManager,
} from "../../auth/accessUtils";

const logoutLogoSvg = `
<svg width="60" height="60" viewBox="0 0 120 120" role="img" aria-label="Yvooa Logo" xmlns="http://www.w3.org/2000/svg">
  <title>Yvooa Logo</title>
  <defs>
    <radialGradient id="coinFaceRound" cx="50%" cy="50%" r="60%">
      <stop offset="0%" stop-color="#e3f2fd" stop-opacity="0.7" />
      <stop offset="50%" stop-color="#1976d2" stop-opacity="0.8" />
      <stop offset="100%" stop-color="#0d47a1" />
    </radialGradient>
    <linearGradient id="yGoldRound" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#F9C642" />
      <stop offset="100%" stop-color="#FFD700" />
    </linearGradient>
  </defs>
  <circle cx="60" cy="60" r="50" fill="url(#coinFaceRound)" />
  <polygon points="53,95 60,30 75,30" fill="#fff" />
  <polygon points="35,30 55,55 50,75" fill="url(#yGoldRound)" />
</svg>
`;
const logoutLogoDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(
  logoutLogoSvg,
)}`;

const roundLogoSvg = `
<svg width="32" height="32" viewBox="0 0 120 120" role="img" aria-label="Yvooa" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="roundGradient" cx="50%" cy="50%" r="60%">
      <stop offset="0%" stop-color="#e3f2fd" stop-opacity="0.7" />
      <stop offset="50%" stop-color="#1976d2" stop-opacity="0.8" />
      <stop offset="100%" stop-color="#0d47a1" />
    </radialGradient>
    <linearGradient id="yGoldRoundLogo" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#F9C642" />
      <stop offset="100%" stop-color="#FFD700" />
    </linearGradient>
  </defs>
  <circle cx="60" cy="60" r="55" fill="url(#roundGradient)" />
  <polygon points="53,95 60,30 75,30" fill="#fff" />
  <polygon points="35,30 55,55 50,75" fill="url(#yGoldRoundLogo)" />
</svg>
`;

const ModernNavbar = () => {
  const userInfo = useRecoilValue(userData);
  const userReset = useResetRecoilState(userData);
  const profileImage = useRecoilValue(profileImageUrl);
  const resetAccessToken = useResetRecoilState(accessToken);
  const resetIdToken = useResetRecoilState(idToken);
  const resetRefreshToken = useResetRecoilState(refreshToken);
  const resetPortfolioTree = useResetRecoilState(portfolioTreeStore);
  const navigate = useNavigate();
  const location = useLocation();

  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [expandedMobileItem, setExpandedMobileItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  // Use notification hook — it reads/writes the shared atom directly,
  // so no sync effects are needed.
  const {
    notifications,
    fetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  } = useNotification(userInfo?.ID);

  const profileRef = useRef(null);
  const menuContainerRef = useRef(null);

  const allMenuItems = useMenuItems();
  const menuItems = allMenuItems;
  const primaryMobileItems = allMenuItems.filter((item) => item.isPrimary);
  const secondaryMobileItems = allMenuItems.filter((item) => !item.isPrimary);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
      // Close dropdown menus when clicking outside
      const dropdownElements = document.querySelectorAll(
        "[data-dropdown-menu]",
      );
      let clickedInsideDropdown = false;
      dropdownElements.forEach((el) => {
        if (el.contains(event.target)) {
          clickedInsideDropdown = true;
        }
      });
      if (!clickedInsideDropdown) {
        setHoveredItem(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (NotificationsMenuFlag && userInfo?.ID) {
      fetchNotifications("received");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.ID]);

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      pendingRequestQueue.reset();
      // Clear all Recoil atoms
      userReset();
      resetAccessToken();
      resetIdToken();
      resetRefreshToken();
      resetPortfolioTree();

      sessionStorage.clear();
      //localStorage.clear();

      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      triggerSnackbar("Failed to sign out", "error");
    }
  };

  const confirmLogout = () => {
    setProfileOpen(false);
    setLogoutDialogOpen(true);
  };

  const showYvooaInfo = () => {
    YvooaInfoModal();
  };

  const isActive = (path) => {
    if (path === "/dashboard") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isChildActive = (childPath) => {
    return (
      location.pathname === childPath || location.pathname.startsWith(childPath)
    );
  };

  const checkScroll = () => {
    if (menuContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = menuContainerRef.current;
      setShowLeftScroll(scrollLeft > 10);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [menuItems]);

  // Convert vertical mouse wheel to horizontal scroll when over the menu
  const handleWheelScroll = (e) => {
    const el = menuContainerRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    if (!hasOverflow) return;
    const dominantDelta =
      Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if (dominantDelta !== 0) {
      e.preventDefault();
      el.scrollBy({ left: dominantDelta, behavior: "smooth" });
    }
  };

  // Keep the active item in view when route changes
  useEffect(() => {
    const el = menuContainerRef.current;
    if (!el) return;
    const active = el.querySelector('[data-active="true"]');
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
    checkScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      <IdleTimeoutContainer handleLogout={handleLogout} />
      <NotificationContainer />

      {/* Desktop Navbar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border-b border-gray-200 dark:border-slate-700 shadow-sm dark:shadow-lg transition-colors duration-300 hidden md:block"
        style={{ height: "4rem" }}
      >
        <div className="w-full h-full mx-auto px-3 lg:px-6 max-w-full">
          <div className="flex items-center gap-2 lg:gap-4 h-full max-w-full">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-pointer flex-shrink-0"
              onClick={showYvooaInfo}
            >
              <img
                src={`data:image/svg+xml;utf8,${encodeURIComponent(roundLogoSvg)}`}
                alt="Yvooa"
                className="h-8 lg:h-10 w-8 lg:w-10"
              />
            </motion.div>

            {/* Navigation Menu with Scroll Indicators */}
            <div className="relative flex-1 min-w-0 overflow-hidden">
              {/* Left Scroll Indicator */}
              {showLeftScroll && (
                <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white/95 dark:from-slate-800/95 to-transparent z-10 pointer-events-none" />
              )}

              {/* Right Scroll Indicator */}
              {showRightScroll && (
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white/95 dark:from-slate-800/95 to-transparent z-10 pointer-events-none" />
              )}

              <div
                ref={menuContainerRef}
                className="flex items-center gap-1 overflow-x-auto overflow-y-hidden scrollbar-hide h-full"
                onScroll={checkScroll}
                onWheel={handleWheelScroll}
              >
                {menuItems.map(
                  (item, index) =>
                    !item.hide && (
                      <div
                        key={item.id}
                        className="relative"
                        style={{ position: "static" }}
                        data-dropdown-menu
                        onMouseEnter={() =>
                          item.children && setHoveredItem(index)
                        }
                        onMouseLeave={() =>
                          item.children && setHoveredItem(null)
                        }
                      >
                        {item.children ? (
                          <DropdownMenu
                            item={item}
                            isActive={isActive(item.path)}
                            isHovered={hoveredItem === index}
                            onNavigate={() => setHoveredItem(null)}
                            isChildActive={isChildActive}
                          />
                        ) : (
                          <Link to={item.path}>
                            <motion.div
                              id={item.id}
                              data-cy={item.id}
                              whileHover={{ y: -2 }}
                              whileTap={{ scale: 0.95 }}
                              data-active={
                                isActive(item.path) ? "true" : "false"
                              }
                              className={`flex items-center gap-1.5 px-2.5 lg:px-3 py-2 rounded-lg transition-all whitespace-nowrap ${
                                isActive(item.path)
                                  ? "bg-blue-600 text-white shadow-md"
                                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-gray-300"
                              }`}
                            >
                              <span className="text-base lg:text-lg">
                                {item.icon}
                              </span>
                              <span className="text-xs lg:text-sm font-medium">
                                {item.label}
                              </span>
                            </motion.div>
                          </Link>
                        )}
                      </div>
                    ),
                )}
              </div>

              {/* Scroll Buttons */}
              {showLeftScroll && (
                <button
                  type="button"
                  id="leftScroll"
                  data-cy="scrollLeft"
                  className="absolute left-1 top-1/2 -translate-y-1/2 z-30 p-1.5 rounded-full bg-white/70 dark:bg-slate-700/70 shadow hover:bg-white dark:hover:bg-slate-700 transition"
                  onClick={() =>
                    menuContainerRef.current?.scrollBy({
                      left: -Math.round(
                        menuContainerRef.current.clientWidth * 0.6,
                      ),
                      behavior: "smooth",
                    })
                  }
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                </button>
              )}
              {showRightScroll && (
                <button
                  type="button"
                  id="rightScroll"
                  data-cy="scrollRight"
                  className="absolute right-1 top-1/2 -translate-y-1/2 z-30 p-1.5 rounded-full bg-white/70 dark:bg-slate-700/70 shadow hover:bg-white dark:hover:bg-slate-700 transition"
                  onClick={() =>
                    menuContainerRef.current?.scrollBy({
                      left: Math.round(
                        menuContainerRef.current.clientWidth * 0.6,
                      ),
                      behavior: "smooth",
                    })
                  }
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                </button>
              )}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink-0">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Notifications */}
              {NotificationsMenuFlag && (
                <motion.button
                  data-cy="notifications"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setNotifOpen(true)}
                  className="relative p-1.5 lg:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <Bell className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600 dark:text-gray-400" />
                  {notifications.some((notif) => !notif.isRead) && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </motion.button>
              )}

              {/* Profile */}
              <div ref={profileRef} className="relative">
                <motion.button
                  id="profileImage"
                  data-cy="profileImage"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-1.5 lg:gap-2 p-1.5 lg:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <Avatar className="w-7 h-7 lg:w-9 lg:h-9">
                    <AvatarImage src={profileImage} alt="Profile" />
                    <AvatarFallback>
                      {userInfo?.firstName?.[0]}
                      {userInfo?.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown
                    className={`w-3 h-3 lg:w-4 lg:h-4 text-gray-600 dark:text-gray-400 transition-transform ${
                      profileOpen ? "rotate-180" : ""
                    }`}
                  />
                </motion.button>

                <AnimatePresence>
                  {profileOpen && (
                    <ProfileDropdown
                      userInfo={userInfo}
                      onProfile={() => {
                        navigate("/myprofile");
                        setProfileOpen(false);
                      }}
                      onSubscription={() => {
                        navigate("/subscription/Details");
                        setProfileOpen(false);
                      }}
                      onProvisioning={() => {
                        navigate("/provisioning");
                        setProfileOpen(false);
                      }}
                      onLogout={confirmLogout}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Top Navbar - Only Logo, Notifications, Avatar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border-b border-gray-200 dark:border-slate-700 shadow-sm md:hidden"
        style={{ height: "4rem" }}
      >
        <div className="w-full h-full mx-auto px-3 max-w-full">
          <div className="flex items-center justify-between gap-2 h-full max-w-full">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-pointer flex-shrink-0"
              onClick={showYvooaInfo}
            >
              <img
                src={`data:image/svg+xml;utf8,${encodeURIComponent(roundLogoSvg)}`}
                alt="Yvooa"
                className="h-8 w-8"
              />
            </motion.div>

            {/* Right Section - Theme, Notifications & Profile */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Notifications */}
              {NotificationsMenuFlag && (
                <motion.button
                  data-cy="notifications"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setNotifOpen(true)}
                  className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  {notifications.some((notif) => !notif.isRead) && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </motion.button>
              )}

              {/* Profile */}
              <div ref={profileRef} className="relative">
                <motion.button
                  id="profileImage"
                  data-cy="profileImage"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <Avatar className="w-7 h-7">
                    <AvatarImage src={profileImage} alt="Profile" />
                    <AvatarFallback>
                      {userInfo?.firstName?.[0]}
                      {userInfo?.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                </motion.button>

                <AnimatePresence>
                  {profileOpen && (
                    <ProfileDropdown
                      userInfo={userInfo}
                      onProfile={() => {
                        navigate("/myprofile");
                        setProfileOpen(false);
                      }}
                      onSubscription={() => {
                        navigate("/subscription/Details");
                        setProfileOpen(false);
                      }}
                      onProvisioning={() => {
                        navigate("/provisioning");
                        setProfileOpen(false);
                      }}
                      onLogout={confirmLogout}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Bottom Navbar - Primary Items + More */}
      <motion.nav
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border-t border-gray-200 dark:border-slate-700 shadow-lg md:hidden"
        style={{ height: "4rem" }}
      >
        <div className="w-full h-full px-2 max-w-full">
          <div className="flex items-center justify-between h-full max-w-full">
            {/* Primary Mobile Menu Items */}
            {primaryMobileItems.map((item) => {
              const hasChildren = item.children && item.children.length > 0;

              if (hasChildren) {
                // Items with children - show as buttons to expand
                return (
                  <motion.button
                    key={item.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() =>
                      setExpandedMobileItem(
                        expandedMobileItem?.id === item.id ? null : item,
                      )
                    }
                    className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg transition-all h-full ${
                      expandedMobileItem?.id === item.id
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700"
                    }`}
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span className="text-xs font-medium truncate max-w-14">
                      {item.label}
                    </span>
                  </motion.button>
                );
              }

              // Regular items without children - navigate on click
              return (
                <Link key={item.id} to={item.path} className="flex-1">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    data-active={isActive(item.path) ? "true" : "false"}
                    className={`flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg transition-all h-full ${
                      isActive(item.path)
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span className="text-xs font-medium truncate max-w-14">
                      {item.label}
                    </span>
                  </motion.div>
                </Link>
              );
            })}

            {/* More Button - Opens Drawer */}
            {secondaryMobileItems.length > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setMobileDrawerOpen(true)}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all h-full"
              >
                <span className="text-xl">
                  <MoreHorizontal className="w-6 h-6" />
                </span>
                <span className="text-xs font-medium">More</span>
              </motion.button>
            )}
          </div>
        </div>
      </motion.nav>

      {/* Mobile Sub-Options Modal - Shows when clicking items with children */}
      <AnimatePresence>
        {expandedMobileItem && expandedMobileItem.children?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-16 left-0 right-0 z-50 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 shadow-2xl md:hidden"
          >
            <div className="px-4 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{expandedMobileItem.icon}</span>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {expandedMobileItem.label} Options
                  </h3>
                </div>
                <motion.button
                  whileHover={{ rotate: 90 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setExpandedMobileItem(null)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </motion.button>
              </div>

              {/* Sub-options Grid */}
              <div className="grid grid-cols-2 gap-2">
                {expandedMobileItem.children.map((child, idx) => (
                  <Link key={child.path} to={child.path}>
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setExpandedMobileItem(null)}
                      className={`w-full px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                        isChildActive(child.path)
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600"
                      }`}
                    >
                      {child.label}
                    </motion.button>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Secondary Items Drawer - More Options */}
      <Sheet open={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen}>
        <SheetContent
          side="bottom"
          className="h-auto max-h-[70vh] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>More Options</SheetTitle>
          </SheetHeader>

          {/* Theme Toggle Section */}
          <div className="flex items-center justify-between p-4 mt-4 rounded-lg bg-gray-50 dark:bg-slate-700">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Theme
            </span>
            <ThemeToggle />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            {secondaryMobileItems.map((item) => {
              // If item has children, show as expandable section indicator
              if (item.children?.length > 0) {
                return (
                  <motion.div
                    key={item.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 text-blue-700 dark:text-blue-400 transition-all cursor-default border border-blue-200 dark:border-blue-700"
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-sm font-medium text-center line-clamp-2">
                      {item.label}
                    </span>
                    <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                      {item.children.length} items
                    </span>
                  </motion.div>
                );
              }

              // Regular items without children
              return (
                <Link key={item.id} to={item.path}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setMobileDrawerOpen(false)}
                    data-active={isActive(item.path) ? "true" : "false"}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg transition-all ${
                      isActive(item.path)
                        ? "bg-blue-600 text-white"
                        : "bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600"
                    }`}
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-sm font-medium text-center line-clamp-2">
                      {item.label}
                    </span>
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent
          showCloseButton={false}
          className="w-[92vw] max-w-[400px] p-8 text-center overflow-hidden rounded-xl"
        >
          {/* Centered vertical layout */}
          <div className="flex flex-col items-center gap-5">
            {/* Logo */}
            <div className="w-[80px] h-[80px] rounded-full overflow-hidden shadow-md ring-4 ring-blue-100">
              <img
                src={logoutLogoDataUrl}
                alt="Yvooa"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Title & Description */}
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white tracking-tight">
                Confirm Logout
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                Are you sure you want to log out? You will need to sign in again
                to continue.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 w-full">
              <button
                onClick={async () => {
                  setLogoutDialogOpen(false);
                  await handleLogout();
                }}
                data-cy="confirmLogout"
                className="flex-1 py-2.5 px-4 bg-[#006DBA] hover:bg-[#025fa1] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-[#006DBA] focus:ring-offset-2"
              >
                Log Out
              </button>
              <button
                onClick={() => setLogoutDialogOpen(false)}
                data-cy="cancelLogout"
                className="flex-1 py-2.5 px-4 bg-white dark:bg-transparent border border-[#006DBA] text-[#006DBA] text-sm font-semibold rounded-lg hover:bg-[#006DBA] hover:text-white dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#006DBA] focus:ring-offset-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notifications Sheet */}
      <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
        <SheetContent
          side="right"
          className="w-[400px] sm:w-[480px] p-0 flex flex-col"
        >
          <NotificationSheetContent
            notifications={notifications}
            onMarkAsRead={(pk) => markNotificationAsRead(pk)}
            onMarkAllAsRead={() => markAllNotificationsAsRead()}
            onClose={() => setNotifOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

// Dropdown Menu Component
const DropdownMenu = ({
  item,
  isActive,
  isHovered,
  onNavigate,
  isChildActive,
}) => {
  const buttonRef = React.useRef(null);
  const [dropdownStyle, setDropdownStyle] = React.useState({});
  const navigate = useNavigate();

  React.useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: `${rect.bottom + 8}px`,
        left: `${rect.left}px`,
      });
    }
  }, [isHovered]);

  const handleChildClick = (childPath) => {
    navigate(childPath);
    onNavigate();
  };

  return (
    <div className="relative">
      <motion.div
        ref={buttonRef}
        id={item.id}
        data-cy={item.id}
        whileHover={{ y: -2 }}
        data-active={isActive ? "true" : "false"}
        className={`flex items-center gap-1.5 px-2.5 lg:px-3 py-2 rounded-lg cursor-pointer transition-all whitespace-nowrap ${
          isActive
            ? "bg-blue-600 text-white shadow-md"
            : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-gray-300"
        }`}
      >
        <span className="text-base lg:text-lg">{item.icon}</span>
        <span className="text-xs lg:text-sm font-medium">{item.label}</span>
        <ChevronDown
          className={`w-3 h-3 lg:w-4 lg:h-4 transition-transform ${
            isActive ? "text-white" : "text-gray-600 dark:text-gray-400"
          } ${isHovered ? "rotate-180" : ""}`}
        />
      </motion.div>

      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            style={dropdownStyle}
            className="w-fit min-w-[10rem] bg-white dark:bg-slate-700 rounded-xl shadow-lg border border-gray-200 dark:border-slate-600 overflow-hidden z-[60]"
          >
            {item.children.map((child, idx) => {
              const isChildItemActive = isChildActive(child.path);
              return (
                <div
                  key={idx}
                  id={child.id}
                  data-cy={child.id}
                  onClick={() => handleChildClick(child.path)}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                    isChildItemActive
                      ? "bg-blue-600 text-white font-medium"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600"
                  }`}
                >
                  <ChevronRight
                    className={`w-3 h-3 flex-shrink-0 ${
                      isChildItemActive
                        ? "text-white/70"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  />
                  <span className="whitespace-nowrap">{child.label}</span>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Profile Dropdown Component
const ProfileDropdown = ({
  userInfo,
  onProfile,
  onSubscription,
  onProvisioning,
  onLogout,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-slate-700 rounded-xl shadow-xl border border-gray-200 dark:border-slate-600 overflow-hidden"
    >
      <div className="p-4 border-b border-gray-200 dark:border-slate-600">
        <p className="font-semibold text-gray-900 dark:text-white">
          {userInfo?.firstName} {userInfo?.lastName}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 break-all">
          {userInfo?.email}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          {userInfo?.userRole || "N/A"}
        </p>
      </div>

      <div className="py-2">
        <button
          id="myProfile"
          data-cy="myProfile"
          onClick={onProfile}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
        >
          <User className="w-4 h-4" />
          My Profile
        </button>
        {!["Property Manager", "Assistant Property Manager"].includes(
          userInfo?.userRole,
        ) && (
          <button
            id="subscription"
            data-cy="subscription"
            onClick={onSubscription}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
          >
            <CreditCard className="w-4 h-4" />
            Subscription
          </button>
        )}
        <button
          id="provisioning"
          data-cy="provisioning"
          onClick={onProvisioning}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
        >
          <Settings2 className="w-4 h-4" />
          Provisioning
        </button>
      </div>

      <div className="border-t border-gray-200 dark:border-slate-600">
        <button
          id="logOut"
          data-cy="logOut"
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-600 transition-colors font-medium"
        >
          <LogOut className="w-4 h-4" />
          Log Out
        </button>
      </div>
    </motion.div>
  );
};

export default ModernNavbar;
